import React, {forwardRef, PropsWithChildren, useCallback, useMemo, useRef, useState, useEffect, useLayoutEffect} from 'react';
import DataGrid, {Column, Paging, Scrolling, SearchPanel, Selection, Toolbar, Item, Grouping, ToolbarItem, FilterPanel, RemoteOperations, DataGridRef} from "devextreme-react/data-grid";

import DataSource from "devextreme/data/data_source";
import {useContextPopupPage} from "../../components/popups/PopupPage";
import dxDataGrid, {CellPreparedEvent, ContentReadyEvent, EditorPreparingEvent, Row} from "devextreme/ui/data_grid";
import {Button} from 'devextreme-react/button';
import {Tooltip, ITooltipOptions} from 'devextreme-react/tooltip';
import {useContextLeftSidebarData} from '../../contexts/refreshDataSource';
import {CheckBox} from "devextreme-react/check-box";

// constant d'animation de Tooltip
const animationConfig: ITooltipOptions['animation'] = {
    show: {
        type: 'fade',
        from: {
            top: -100,
            opacity: 0,
        },
        to: {
            top: 0,
            opacity: 1,
        },
    },
    hide: {
        type: 'pop',
        from: {
            scale: 1,
            opacity: 1,
        },
        to: {
            scale: 0.1,
            opacity: 0,
        },
    },
};


interface FormListBaseGridProps {
    dataSource?: DataSource,
    onFocusedRowChanged?: any,
    onRowDoubleClick?: any,
    onCellPrepared?: any,
    focusedRowKey?: any,
    keyExpr?: string,
    defaultSearch?: string,
    legend?: any,
    afficherExtra?: string[]
}

const FormListBaseGrid = forwardRef<DataGridRef, FormListBaseGridProps & PropsWithChildren>(({dataSource, children, legend, onFocusedRowChanged, onCellPrepared, focusedRowKey, keyExpr, onRowDoubleClick, defaultSearch, afficherExtra}, ref): any => {
    const [defaultSearchInitialized, setDefaultSearchInitialized] = useState(false)
    const {setDisplayedElementInactif, displayedElementInactif} = useContextLeftSidebarData()
    const [filterValue, setFilterValue] = useState<any[]>([]);
    const {isContextInitialized} = useContextPopupPage();
    const [searchBox, setSearchBox] = useState<any>(null);
    const [dataGridFocusedRowKey, setDataGridFocusedRowKey] = useState(focusedRowKey);
    const [firstSearch, setFirstSearch] = useState(true)
    const [afficherInactif, setAfficherInactif] = useState(false);

    // Focus et highlight un nouveau element créé
    useEffect(() => {
        if (focusedRowKey) {
            setDataGridFocusedRowKey(focusedRowKey);
        }
    }, [focusedRowKey]);

    const onEditorPreparing = useCallback((e: EditorPreparingEvent) => {
        if (e.parentType == "searchPanel") {
            e.editorOptions.onContentReady = (p: ContentReadyEvent) => {
                setSearchBox(p.component);
            };
        }
    }, []);
    const _onFocusedRowChanged = useCallback((e: any) => {
        onFocusedRowChanged(e)
        setDataGridFocusedRowKey(e.component.option('focusedRowKey'));
    }, [onFocusedRowChanged])

    const itHasActiveData = useMemo(() => dataSource?.items()[0]?.hasOwnProperty('actif'), [dataSource?.items()]);

    const displayInactifItems = useCallback(async (isDisplayedElementInactif: boolean) => {
        // Met à jour l'état 'setDisplayedElementInactif' dans le context avec la valeur de 'isDisplayedElementInactif', indiquant si les éléments inactifs doivent être affichés ou non.
        setDisplayedElementInactif(isDisplayedElementInactif);
    }, [setDisplayedElementInactif]);

    const onContentReadyGrid = useCallback((c: ContentReadyEvent) => {
        if (isContextInitialized && firstSearch) {
            searchBox?.focus();
            dataGridRef.current?.instance().searchByText(defaultSearch?.replace("Aucun", "").trim() || "");
            setFirstSearch(false);
        }

    }, [searchBox, isContextInitialized, defaultSearch, firstSearch]);

    useLayoutEffect(() => {
        if (displayedElementInactif) {
            dataSource?.filter(null);
            dataGridRef?.current?.instance().repaint()
        } else if (itHasActiveData) {
            dataSource?.filter(["actif", "=", true]);
            dataGridRef?.current?.instance().repaint()
        }
    }, [displayedElementInactif, itHasActiveData]);

    const style = useMemo(() => ({height: "100%", flex: "1 0 0"}), [isContextInitialized]);
    const dataGridRef = useRef<DataGridRef>(null);
    let wordSearch: string[] = [];
    const calculateFilterExpression = useCallback((filterValue: any, selectedFilterOperation: (string | null), target: string, datafield: string) => {
        if (target !== 'search' || typeof filterValue !== "string") {
            return;
        }

        if (target === 'search') {
            if (filterValue.trim().length > 0) {
                const words = filterValue.split(' ').filter((k: any) => k !== '');
                wordSearch = words;
                let filter: any = [];
                words.forEach((word) => {
                    filter.push([datafield, 'contains', word]);
                    filter.push('or');
                });
                filter.pop();
                return filter;
            }
        }
    }, [wordSearch])

    function splitWithoutDeleting(string: string, substring: string) {
        let index = string.toLowerCase().indexOf(substring.toLowerCase());
        let startIndex = index;
        let endIndex = index + substring.length;
        if (startIndex === -1) return [string]
        let arr = [string.substring(0, startIndex), string.substring(startIndex, endIndex), string.substring(endIndex, string.length)];
        if (startIndex === 0) arr.shift();
        if (endIndex === string.length) arr.pop();
        return arr;
    }

    const customCellTemplate = useCallback((cellElement: any, cellInfo: {
        column: typeof Column;
        columnIndex: number;
        component: dxDataGrid;
        data: Record<string, any>;
        displayValue: any;
        oldValue: any;
        row: Row;
        rowIndex: number;
        rowType: string;
        text: string;
        value: any;
        watch: (() => void);
    }) => {
        let arr: any = [cellInfo.text];
        if (wordSearch) {
            wordSearch.forEach(word => {
                for (let i = 0; i < arr.length; i++) {
                    if (arr[i].toLowerCase().includes(word.toLowerCase())) {
                        arr.splice(i, 1, ...splitWithoutDeleting(arr[i], word))
                    }
                }
            });
            for (let i = 0; i < arr.length; i++) {
                arr[i] = {string: arr[i], highlight: 0}
                wordSearch.forEach(word => {
                    if (arr[i].string.toLowerCase().includes(word.toLowerCase())) {
                        arr[i].highlight = 1;
                    }
                })
            }
            for (let i = 0; i < arr.length; i++) {
                if (arr[i].highlight) {
                    let highlightArray = ['<span class="dx-datagrid-search-text">', arr[i].string, '</span>'];
                    arr.splice(i, 1, ...highlightArray)
                } else if (typeof arr[i].string === "string" || arr[i].string instanceof String) {
                    arr[i] = arr[i].string;
                }
            }
        }
        if (!dataGridRef.current?.instance()?.option("searchPanel.text")) {
            let el = document.createElement('span');
            el.innerHTML = cellInfo.text.toString();
            cellElement.append(el);
        } else {
            let el = document.createElement('span');
            el.innerHTML = arr ? arr.join('').toString() : cellInfo.text.toString();
            cellElement.append(el);
        }
    }, [wordSearch]);

    const customizeColumns = useCallback((columns: any) => {
        columns.forEach((col: any) => {
            col.calculateFilterExpression = (filterValue: any, selectedFilterOperation: string | null, target: string) => calculateFilterExpression(filterValue, selectedFilterOperation, target, col.name);
            if (col.dataType && col.dataType !== "object") {
                col.cellTemplate = customCellTemplate;
            }
        })
    }, [calculateFilterExpression, customCellTemplate]);

    const onCellPreparedBase = useCallback((e: CellPreparedEvent) => {
        if (onCellPrepared)
            onCellPrepared(e)
        if (e.rowType == "data" && itHasActiveData) {
            if (!e.data.actif) {
                e.cellElement.style.backgroundColor = "#cccccc"
            }
        }
    }, [onCellPrepared, itHasActiveData]);
    const afficherInactifonValueChange = useCallback(() => {
        setAfficherInactif(prev => {
            displayInactifItems(!prev);
            return !prev
        })
    }, [displayInactifItems, afficherInactif]);
    const AfficherToutrender = useCallback((params: any) => {
        return <div>
            <CheckBox
                id="truncate-button-1"
                width={itHasActiveData ? '100px' : 'auto'}
                text='Afficher tout'
                // onValueChange={() => displayInactifItems(false)}
            >
                <Tooltip
                    target="#truncate-button-1"
                    showEvent="mouseenter"
                    hideEvent="mouseleave"
                    animation={animationConfig}
                    hideOnOutsideClick={false}
                >
                    <div>Afficher tout</div>
                </Tooltip>
            </CheckBox>
            <CheckBox
                visible={itHasActiveData}
                id="truncate-button-2"
                width={'auto'}
                text='Afficher les éléments inactifs'
                onValueChange={afficherInactifonValueChange}
                value={afficherInactif}
            >
                <Tooltip
                    target="#truncate-button-2"
                    showEvent="mouseenter"
                    hideEvent="mouseleave"
                    animation={animationConfig}
                    hideOnOutsideClick={false}
                >
                    <div>Afficher les éléments inactifs</div>
                </Tooltip>
            </CheckBox>
            {afficherExtra?.map((item) => (
                <CheckBox
                    visible={itHasActiveData}
                    id={`truncate-button-${item}`}
                    width={'auto'}
                    text={`Afficher ${item}`}
                    // onValueChange={(value) => displayInactifItems(value || false)}
                >
                    <Tooltip
                        target={`#truncate-button-${item}`}
                        showEvent="mouseenter"
                        hideEvent="mouseleave"
                        animation={animationConfig}
                        hideOnOutsideClick={false}
                    >
                        <div>Afficher {item}</div>
                    </Tooltip>
                </CheckBox>
            ))}
        </div>
    }, [itHasActiveData, displayInactifItems, afficherInactif]);

    return <>
        <DataGrid
            ref={dataGridRef}
            style={style}
            dataSource={dataSource}
            focusedRowEnabled={true}
            defaultFocusedRowIndex={0}
            onFocusedRowChanged={_onFocusedRowChanged}
            onRowDblClick={onRowDoubleClick}
            autoNavigateToFocusedRow={true}
            className="list-data-grid"
            height={"inherit"}
            onCellPrepared={onCellPreparedBase}
            onEditorPreparing={onEditorPreparing}
            focusedRowKey={dataGridFocusedRowKey}
            onContentReady={onContentReadyGrid}
            customizeColumns={customizeColumns}
            allowColumnResizing={true}
            cellHintEnabled={true}
        >
            <Paging enabled={false}/>
            <SearchPanel visible={true} highlightSearchText={true}/>
            <Selection selectAllMode={"page"}/>
            <Scrolling mode="virtual" showScrollbar="always"/>
            {itHasActiveData ? <Column dataField={"actif"} visible={false}/> : null}
            {/* Barre d'outils */}
            <Toolbar>
                {/* Bouton "Afficher tout" */}
                <Item render={AfficherToutrender}/>
                {/* Champ de recherche */}
                <Item name="searchPanel" location={"before"}/>
            </Toolbar>
            {children}
        </DataGrid>
        {legend}
    </>
})

export default FormListBaseGrid;